import clsx from 'clsx';
import { jwtDecode } from 'jwt-decode';
// import { IoChatboxEllipses } from 'react-icons/io5';

import { Edit, X } from 'lucide-react';
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { BiCategoryAlt } from 'react-icons/bi';
import { FaUsers } from 'react-icons/fa';
import { IoIosArrowBack, IoMdPeople } from 'react-icons/io';
import { IoDocumentText } from 'react-icons/io5';
// import { CgInsights } from 'react-icons/cg';
import { useDispatch, useSelector } from 'react-redux';
import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import CardComponent from '@/components/card.component';
import CustomStatusDropdown from '@/components/customStatusDropdown';
import { ProjectMenuItemsSkeleton, ProjectTitleSkeleton, ProjectsCardSkeleton } from '@/components/skeleton.effect';

import { keyConst } from '@/constants';

import { useClientPreview } from '@/hooks/useClientPreview';
import { useProjectPermissions } from '@/hooks/usePermission';

// import { MdOutlineSchedule } from 'react-icons/md';
import { DecodedToken, UpdateProjectForm } from '@/interface';

import EditProjectModal from '@/pages/dashboard/project/detail/components/editProjectModal';

import { assignProject, unassignProject } from '@/redux/slices';
import { getPermissionsRequest } from '@/redux/slices/auth.slice';
import { getOverviewRequest } from '@/redux/slices/overview.slice';
import { getProjectDetailById, getProjectsRequest, updateProjectRequest } from '@/redux/slices/project.slice';
import {
  setHasUnreadSuggestedTenant,
  setProjectChatUnseenCount,
  setSuggestedTenantUnseenCounts,
} from '@/redux/slices/projectChat.slice';
import { AppDispatch, RootState } from '@/redux/store';

const MENU_ITEMS = (projectId: string, showFilters: boolean) => {
  if (showFilters) {
    return [
      {
        icon: BiCategoryAlt,
        text: 'Suggested Experts',
        path: `/project-detail/${projectId}/suggested-experts`,
        id: 1,
        permissionKey: 'suggested_experts',
      },
    ];
  }
  return [
    {
      icon: IoDocumentText,
      text: 'Overview',
      path: `/project-detail/${projectId}/overview`,
      id: 1,
    },
    {
      icon: IoMdPeople,
      text: 'Experts',
      path: `/project-detail/${projectId}/experts`,
      id: 2,
      permissionKey: 'view_expert',
    },
    {
      icon: FaUsers,
      text: 'Team Members',
      path: `/project-detail/${projectId}/team-members`,
      id: 3,
      permissionKey: 'view_team_member',
    },
    // {
    //   icon: IoChatboxEllipses,
    //   text: 'Chat',
    //   path: `/project-detail/${projectId}/chat`,
    //   id: 5,
    //   permissionKey: 'view_message',
    // },
    // {
    //   icon: MdOutlineSchedule,
    //   text: 'Schedule Meetings',
    //   path: `/project-detail/${projectId}/schedule-meetings`,
    //   permissionKey: 'view_schedule_meeting',
    //   id: 6,
    // },
    // {
    //   icon: CgInsights,
    //   text: 'Insights',
    //   path: `/project-detail/${projectId}/insights`,
    //   permissionKey: 'view_insights',
    //   id: 7,
    // },
    {
      icon: FaUsers,
      text: 'Suggested Tanent',
      path: `/project-detail/${projectId}/suggested-tenant`,
      id: 8,
      // permissionKey: 'view_suggested_tenant',
    },
  ];
};

export const ProjectDetailLayout = React.memo(() => {
  const [modalOpen, setModalOpen] = useState(false);
  const [assigningProject, setAssigningProject] = useState(false);
  const [unassigningProject, setUnassigningProject] = useState(false);
  const [isSwitchingProject, setIsSwitchingProject] = useState(false);
  const [isPermissionResolving, setIsPermissionResolving] = useState(false);
  // const [unseenCounts, setUnseenCounts] = useState<Map<string, number>>(new Map());

  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { projectDetail, loading, error } = useSelector((state: RootState) => state.project);
  const activeTab = location.pathname.split('/').pop();

  const { userToken, userId, companyId: authCompanyId } = useSelector((state: RootState) => state.auth);
  const activeToken = localStorage.getItem(keyConst.userToken) || userToken;
  const decoded = activeToken ? jwtDecode<DecodedToken>(activeToken) : null;
  const userType = decoded?.userType;
  const activeUserId = decoded?.userId || userId;
  const { isClientPreview } = useClientPreview();
  const {
    hasUnreadSuggestedTenant,
    projectChatUnseenCount,
    hasUnreadProjectChat,
    suggestedTenantUnseenCountsByProject,
    projectChatUnseenCountsByProject,
  } = useSelector((state: RootState) => state.projectChat);
  const hasPermission = useProjectPermissions();
  const canUpdateProject = hasPermission('update_project');
  const resolvedPermissionCompanyId =
    location.state?.companyId ?? projectDetail?.companyId ?? projectDetail?.company?.id ?? authCompanyId ?? null;

  // const socketConnectedRef = React.useRef(false);
  // const suggestedTenantCountsRef = React.useRef<Record<string, number>>({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  // const projectChatHandlersRef = React.useRef<{
  //   handleUnseenCountUpdate: ((data: any) => void) | null;
  //   handleUnseenCount: ((data: any) => void) | null;
  // }>({ handleUnseenCountUpdate: null, handleUnseenCount: null });

  useEffect(() => {
    if (!activeUserId || !projectId) return;

    setIsPermissionResolving(true);
    dispatch(
      getPermissionsRequest({
        data: {
          userId: activeUserId,
          projectId,
          ...(resolvedPermissionCompanyId ? { companyId: resolvedPermissionCompanyId } : {}),
        },
      }),
    ).finally(() => {
      setIsPermissionResolving(false);
    });
  }, [dispatch, activeUserId, projectId, resolvedPermissionCompanyId]);

  const menuItems = useMemo(() => {
    if (!projectId) return [];

    const showFilters = location.state?.showFilters === true;
    const isPreChatMode = location.state?.clientId && location.state?.companyId;
    let items = MENU_ITEMS(projectId, showFilters);

    if (userType !== 'client') {
      items = items.filter((item) => item.text !== 'Suggested Tanent');
    }

    if (userType === 'client') {
      const isAssigned = projectDetail?.company && projectDetail?.companyId;
      if (!isAssigned) {
        items = items.filter((item) => item.text === 'Suggested Tanent' || (isPreChatMode && item.text === 'Chat'));
      } else {
        items = items.filter(
          (item) =>
            item.text !== 'Overview' &&
            item.text !== 'Suggested Tanent' &&
            item.text !== 'Team Members' &&
            (!item.permissionKey || hasPermission(item.permissionKey)),
        );
      }
    } else if (userType === 'tenant') {
      items = items.filter((item) => item.text !== 'Suggested Tanent');
      if (!projectDetail?.company && !projectDetail?.companyId) {
        const tabsToHide = ['Team Members', 'Insights', 'Schedule Meetings', 'Experts'];
        if (!isPreChatMode) {
          tabsToHide.push('Chat');
        }
        items = items.filter((item) => !tabsToHide.includes(item.text));
      } else {
        items = items.filter((item) => !item.permissionKey || hasPermission(item.permissionKey));
      }
    } else {
      items = items.filter((item) => !item.permissionKey || hasPermission(item.permissionKey));
    }

    if (isClientPreview) {
      items = items.filter((item) => item.text !== 'Team Members' && item.text !== 'Overview');
    }

    return items;
  }, [
    projectId,
    userType,
    location.state?.showFilters,
    location.state?.clientId,
    location.state?.companyId,
    projectDetail,
    hasPermission,
    isClientPreview,
  ]);

  // useEffect(() => {
  //   if (!userToken || !projectId) return;

  //   const socket = io(`${import.meta.env.VITE_BASE_URL}`, {
  //     auth: { token: userToken },
  //     transports: ['websocket'],
  //   });

  //   socketRef.current = socket;

  //   socket.on('connect', () => {
  //     console.log('Socket connected');
  //     socket.emit('joinProjectRoom', { projectId });
  //   });

  //   socket.on('disconnect', () => {
  //     console.log('Socket disconnected');
  //   });

  //   return () => {
  //     socket.disconnect();
  //     socketRef.current = null;
  //   };
  // }, [userToken, projectId]);

  useEffect(() => {
    if (!projectId) return;

    if (projectDetail && projectDetail.currentUserRoleName === null) {
      navigate('/not-found');
    }
    // Socket connection is now managed by chatWindow component only
    // dispatch(connectToProjectSocket({ projectId }));

    // return () => {
    //   dispatch(disconnectFromProjectSocket());
    // };
  }, [dispatch, projectId, projectDetail, navigate]);

  useEffect(() => {
    const handleResize = () => {
      const currentWidth = window.innerWidth;
      setIsMobile(currentWidth < 768);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!projectId || userType !== 'client') return;
    if (activeTab !== 'overview') return;

    const isAssigned = Boolean(projectDetail?.company && projectDetail?.companyId);
    navigate(`/project-detail/${projectId}/${isAssigned ? 'experts' : 'suggested-tenant'}`, { replace: true });
  }, [activeTab, navigate, projectDetail?.company, projectDetail?.companyId, projectId, userType]);

  useEffect(() => {
    if (projectId) {
      setIsSwitchingProject(true);
      dispatch(getProjectDetailById(projectId));
    }
  }, [dispatch, projectId]);

  useEffect(() => {
    if (!loading && isSwitchingProject) {
      const timer = setTimeout(() => {
        setIsSwitchingProject(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [loading, isSwitchingProject]);

  // useEffect(() => {
  //   if (userType !== 'client' || !projectId || !projectDetail || projectDetail.company || projectDetail.companyId) return;
  //
  //   const token = localStorage.getItem(keyConst.userToken);
  //   if (!token) return;
  //
  //   let isSubscribed = true;
  //   let recipientEventSource: SseConnection | undefined;
  //
  //   const setupRecipientEvents = () => {
  //     if (!isSubscribed) return;
  //
  //     socketConnectedRef.current = true;
  //
  //     const handleUnseenCount = (data: any) => {
  //       if (!isSubscribed) return;
  //       const user = JSON.parse(atob(token.split(".")[1]));
  //       const clientUserId = user.userId;
  //
  //       if ((!data.recipientId || data.recipientId === clientUserId) && data.projectId === projectId) {
  //         setUnseenCounts((prev) => {
  //           const newMap = new Map(prev);
  //           newMap.set(data.tenantId, data.count);
  //           return newMap;
  //         });
  //       }
  //     };
  //
  //     recipientEventSource = connectUnifiedRecipientEvents(token, {
  //       onRecipientCountsSnapshot: (data: any) => {
  //         if (!isSubscribed) return;
  //
  //         setUnseenCounts(() => {
  //           const newMap = new Map<string, number>();
  //           (data.preChatCounts || []).forEach((countItem: any) => {
  //             if (countItem.projectId === projectId && countItem.tenantId) {
  //               newMap.set(countItem.tenantId, countItem.count || 0);
  //             }
  //           });
  //           return newMap;
  //         });
  //       },
  //       onUnseenCount: handleUnseenCount,
  //       onUnseenCountUpdate: handleUnseenCount,
  //     });
  //     // onUnseenCount(handleUnseenCount);
  //     // onUnseenCountUpdate(handleUnseenCount);
  //   };
  //
  //   setupRecipientEvents();
  //
  //   return () => {
  //     isSubscribed = false;
  //     socketConnectedRef.current = false;
  //     recipientEventSource?.close();
  //   };
  // }, [userType, projectId, projectDetail, dispatch]);
  //
  // useEffect(() => {
  //   const hasUnread = Array.from(unseenCounts.values()).some(count => count > 0);
  //   dispatch(setHasUnreadSuggestedTenant(hasUnread));
  //   dispatch(setSuggestedTenantUnseenCounts(Object.fromEntries(unseenCounts)));
  // }, [unseenCounts, dispatch]);
  //
  // useEffect(() => {
  //   setUnseenCounts(new Map());
  //   dispatch(setProjectChatUnseenCount(0));
  // }, [projectId, dispatch]);

  // useEffect(() => {
  //   if (!projectId || !userToken) return;
  //
  //   let isSubscribed = true;
  //   let recipientEventSource: SseConnection | undefined;
  //   const user = JSON.parse(atob(userToken.split(".")[1]));
  //   const currentRecipientId = user.clientId || user.userId;
  //   const isClientUnassignedProject =
  //     userType === 'client' && projectDetail && !projectDetail.company && !projectDetail.companyId;
  //
  //   const syncSuggestedTenantCounts = (counts: Record<string, number>) => {
  //     suggestedTenantCountsRef.current = counts;
  //     dispatch(setSuggestedTenantUnseenCounts(counts));
  //     dispatch(setHasUnreadSuggestedTenant(Object.values(counts).some((count) => count > 0)));
  //   };
  //
  //   const updateSuggestedTenantCount = (tenantId: string, count: number) => {
  //     syncSuggestedTenantCounts({
  //       ...suggestedTenantCountsRef.current,
  //       [tenantId]: count,
  //     });
  //   };
  //
  //   const setupRecipientEvents = () => {
  //     if (!isSubscribed) return;
  //     socketConnectedRef.current = true;
  //
  //     const handleUnseenCountUpdate = (data: any) => {
  //       if (!isSubscribed) return;
  //
  //       if (data.recipientId === currentRecipientId && data.projectId === projectId) {
  //         dispatch(setProjectChatUnseenCount(data.count));
  //       }
  //     };
  //
  //     const handleUnseenCount = (data: any) => {
  //       if (!isSubscribed) return;
  //       if (data.projectId === projectId) {
  //         dispatch(setProjectChatUnseenCount(data.count));
  //       }
  //     };
  //
  //     const handlePreChatUnseenCount = (data: any) => {
  //       if (!isSubscribed || !isClientUnassignedProject) return;
  //       if (data.projectId !== projectId || !data.tenantId) return;
  //       if (data.recipientId && data.recipientId !== currentRecipientId) return;
  //
  //       updateSuggestedTenantCount(data.tenantId, data.count);
  //     };
  //
  //     recipientEventSource = connectUnifiedRecipientEvents(userToken, {
  //       onRecipientCountsSnapshot: (data: any) => {
  //         if (!isSubscribed) return;
  //         const countItem = (data.projectChatCounts || []).find((item: any) => item.projectId === projectId);
  //         dispatch(setProjectChatUnseenCount(countItem?.count || 0));
  //
  //         if (isClientUnassignedProject) {
  //           const nextCounts: Record<string, number> = {};
  //           (data.preChatCounts || []).forEach((countItem: any) => {
  //             if (countItem.projectId === projectId && countItem.tenantId) {
  //               nextCounts[countItem.tenantId] = countItem.count || 0;
  //             }
  //           });
  //           syncSuggestedTenantCounts(nextCounts);
  //         }
  //       },
  //       onUnseenCount: handlePreChatUnseenCount,
  //       onUnseenCountUpdate: handlePreChatUnseenCount,
  //       onProjectChatUnseenCount: handleUnseenCount,
  //       onProjectChatUnseenCountUpdate: handleUnseenCountUpdate,
  //     });
  //     // socket.on('projectChatUnseenCountUpdate', handleUnseenCountUpdate);
  //     // socket.on('projectChatUnseenCount', handleUnseenCount);
  //   };
  //
  //   setupRecipientEvents();
  //
  //   return () => {
  //     isSubscribed = false;
  //     socketConnectedRef.current = false;
  //     recipientEventSource?.close();
  //     // const socket = getUnifiedSocket();
  //     // socket?.off('projectChatUnseenCountUpdate', projectChatHandlersRef.current.handleUnseenCountUpdate);
  //     // socket?.off('projectChatUnseenCount', projectChatHandlersRef.current.handleUnseenCount);
  //     // projectChatHandlersRef.current = { handleUnseenCountUpdate: null, handleUnseenCount: null };
  //   };
  // }, [projectId, userToken, userType, projectDetail, dispatch]);

  useEffect(() => {
    if (!projectId) return;

    const suggestedTenantCounts = suggestedTenantUnseenCountsByProject[projectId] || {};
    const currentProjectChatCount = projectChatUnseenCountsByProject[projectId] || 0;

    dispatch(setSuggestedTenantUnseenCounts(suggestedTenantCounts));
    dispatch(setHasUnreadSuggestedTenant(Object.values(suggestedTenantCounts).some((count) => count > 0)));
    dispatch(setProjectChatUnseenCount(currentProjectChatCount));
  }, [projectId, suggestedTenantUnseenCountsByProject, projectChatUnseenCountsByProject, dispatch]);

  // useEffect(() => {
  //   const isOnSuggestedExperts = location.pathname.includes('/suggested-experts');
  //   const isAllowed = location.state?.showFilters === true;

  //   if (isOnSuggestedExperts && !isAllowed) {
  //     navigate(`/project-detail/${projectId}/overview`);
  //   }
  // }, [location.pathname, location.state?.showFilters, navigate, projectId]);

  const handleSave = (
    newTitle: string,
    newDesc: string,
    requestedCalls: number,
    insightsDeskTeam: string,
    insightsDeskPocNames: string[],
    screeningQuestions: UpdateProjectForm['screeningQuestions'],
  ) => {
    const data: UpdateProjectForm = {
      title: newTitle,
      description: newDesc,
      requestedCalls,
      insightsDeskTeam,
      insightsDeskPocNames,
      screeningQuestions,
    };
    dispatch(updateProjectRequest({ id: projectId!, data }));
    setModalOpen(false);
  };

  const handleAcceptProject = async () => {
    if (!projectId) return;
    try {
      setAssigningProject(true);
      const resultAction = await dispatch(assignProject({ projectId }));

      setAssigningProject(false);

      if (assignProject.fulfilled.match(resultAction)) {
        toast.success(`Project "${projectDetail?.title}" accepted successfully!`);
        dispatch(getProjectDetailById(projectId));
        dispatch(getProjectsRequest(true));
        dispatch(getOverviewRequest());
        if (userType === 'client') {
          await dispatch(
            getPermissionsRequest({
              data: {
                userId: decoded?.userId || '',
                projectId,
                ...(resolvedPermissionCompanyId ? { companyId: resolvedPermissionCompanyId } : {}),
              },
            }),
          );
        }

        navigate(`/project-detail/${projectId}/${userType === 'client' ? 'experts' : 'overview'}`);
      } else {
        toast.error(
          typeof resultAction.payload === 'string' ? resultAction.payload : `Failed to accept project "${projectDetail?.title}"`,
        );
      }
    } catch {
      setAssigningProject(false);
      toast.error('Something went wrong while accepting the project.');
    }
  };

  const handleDeclineProject = async () => {
    if (!projectId) return;
    try {
      setUnassigningProject(true);
      const resultAction = await dispatch(unassignProject({ projectId }));

      setUnassigningProject(false);

      if (unassignProject.fulfilled.match(resultAction)) {
        toast.success(`Project "${projectDetail?.title}" declined successfully!`);

        const currentCreatedById = projectDetail?.createdBy?.id;
        const currentCompanyId = projectDetail?.companyId;

        dispatch(getProjectDetailById(projectId));
        dispatch(getProjectsRequest(true));
        // dispatch(getOverviewRequest());
        if (currentCreatedById && decoded?.userId && (decoded?.companyId || currentCompanyId)) {
          const companyId = decoded?.companyId || currentCompanyId;
          navigate(`/project-detail/${projectId}/chat`, {
            state: {
              clientId: userType === 'tenant' ? currentCreatedById : decoded.userId,
              userId: decoded.userId,
              companyId: companyId,
              projectId: projectId,
            },
          });
        } else {
          navigate(`/projects`);
        }
      } else {
        toast.error(
          typeof resultAction.payload === 'string' ? resultAction.payload : `Failed to decline project "${projectDetail?.title}"`,
        );
      }
    } catch {
      setUnassigningProject(false);
      toast.error('Something went wrong while declining the project.');
    }
  };

  return (
    <>
      <Toaster />
      {isMobile ? (
        /* Mobile Layout */
        <div className="flex md:hidden flex-col h-full min-h-0 overflow-hidden">
          <div className="flex-shrink-0">
            <div className="flex items-center px-3 py-2">
              <IoIosArrowBack className="cursor-pointer text-xl flex-shrink-0" onClick={() => navigate('/projects')} />

              <div className="flex-1 min-w-0 ml-2">
                {loading ? (
                  <ProjectTitleSkeleton />
                ) : error ? (
                  <div className="text-red-500 text-sm">Failed to load</div>
                ) : (
                  <h1 className="text-base font-semibold text-black truncate">{projectDetail?.title || 'Untitled Project'}</h1>
                )}
              </div>
            </div>

            {/* Mobile Action Buttons */}
            <div className="px-3 pb-2">
              <div className="flex items-center gap-2 flex-wrap">
                {userType !== 'client' && (
                  <div className="flex-1 min-w-[120px]">
                    <CustomStatusDropdown
                      projectId={projectId!}
                      currentStatus={projectDetail?.status || 'active'}
                      disabled={projectDetail?.currentUserRoleName?.toLowerCase().trim() === 'client'}
                    />
                  </div>
                )}

                {userType === 'tenant' ? (
                  <>
                    {!projectDetail?.company && !projectDetail?.companyId ? (
                      <button
                        type="button"
                        onClick={handleAcceptProject}
                        disabled={assigningProject}
                        className="flex items-center justify-center gap-1.5 rounded-md transition px-3 py-2 text-sm bg-btn text-btn-text hover:bg-btn-hover hover:text-btn-hover-text disabled:opacity-50 whitespace-nowrap"
                      >
                        {assigningProject ? 'Accepting...' : 'Accept'}
                      </button>
                    ) : (
                      <>
                        {projectDetail?.createdBy?.userType === 'client' && canUpdateProject && (
                          <button
                            type="button"
                            onClick={handleDeclineProject}
                            disabled={unassigningProject}
                            className="flex items-center justify-center gap-1 rounded-md transition p-2 bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                            title="Decline Project"
                          >
                            <X size={18} />
                          </button>
                        )}
                      </>
                    )}
                    {(projectDetail?.company || projectDetail?.companyId) && canUpdateProject && (
                      <button
                        type="button"
                        onClick={() => setModalOpen(true)}
                        className="flex items-center justify-center gap-1 rounded-md transition p-2 bg-btn text-btn-text hover:bg-btn-hover hover:text-btn-hover-text"
                        title="Edit Project"
                      >
                        <Edit size={18} />
                      </button>
                    )}
                  </>
                ) : userType === 'client' ? null : (
                  //   <>
                  //     {projectDetail?.company && projectDetail?.companyId && canUpdateProject && (
                  //       <button
                  //         type="button"
                  //         onClick={handleDeclineProject}
                  //         disabled={unassigningProject}
                  //         className="flex items-center justify-center gap-1 rounded-md transition p-2 bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                  //         title="Decline Project"
                  //       >
                  //         <X size={18} />
                  //       </button>
                  //     )}
                  //     {canUpdateProject && (
                  //       <button
                  //         type="button"
                  //         onClick={() => setModalOpen(true)}
                  //         className="flex items-center justify-center gap-1 rounded-md transition p-2 bg-btn text-btn-text hover:bg-btn-hover hover:text-btn-hover-text"
                  //         title="Edit Project"
                  //       >
                  //         <Edit size={18} />
                  //       </button>
                  //     )}
                  //   </>
                  // ) : (
                  canUpdateProject && (
                    <button
                      type="button"
                      onClick={() => setModalOpen(true)}
                      className="flex items-center justify-center gap-1 rounded-md transition p-2 bg-btn text-btn-text hover:bg-btn-hover hover:text-btn-hover-text"
                      title="Edit Project"
                    >
                      <Edit size={18} />
                    </button>
                  )
                )}
              </div>
            </div>

            {/* Mobile Tabs */}
            {isSwitchingProject || isPermissionResolving ? (
              <ProjectMenuItemsSkeleton />
            ) : (
              <div className="overflow-x-auto scrollbar-hide">
                <div className="flex border-b-2 border-gray-200 px-3 min-w-max">
                  {menuItems.map((item) => {
                    // Clear pre-chat state when navigating away from Chat
                    const shouldClearPreChatState = item.text !== 'Chat' && location.state?.clientId && location.state?.companyId;
                    const linkState = shouldClearPreChatState ? undefined : location.state;

                    return (
                      <Link
                        key={item.id}
                        to={item.path}
                        state={linkState}
                        className={`flex items-center px-3 py-2 whitespace-nowrap text-sm flex-shrink-0 transition-colors ${
                          activeTab === item.path.split('/').pop()
                            ? 'border-b-2 border-primary text-primary font-semibold -mb-[2px]'
                            : 'text-gray-500 hover:text-primary'
                        }`}
                      >
                        <item.icon className="mr-2" size={18} />
                        {item.text}
                        {item.text === 'Suggested Tanent' && hasUnreadSuggestedTenant && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                        {item.text === 'Chat' && hasUnreadProjectChat && (
                          <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-2 py-0.5 min-w-[20px] h-5 flex items-center justify-center">
                            {projectChatUnseenCount > 10 ? '10+' : projectChatUnseenCount}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <main
            className={clsx('flex flex-col flex-1 min-h-0 px-2 pt-3 min-w-0', {
              'pointer-events-none cursor-not-allowed':
                projectDetail?.status === 'closed' && projectDetail?.currentUserRoleName !== 'owner',
              '[&_input]:pointer-events-none [&_textarea]:pointer-events-none [&_.ant-select]:pointer-events-none [&_.ant-picker]:pointer-events-none [&_.ant-upload]:pointer-events-none [&_input]:opacity-70 [&_textarea]:opacity-70 [&_.ant-select]:opacity-70 [&_.ant-picker]:opacity-70 [&_.ant-upload]:opacity-70':
                isClientPreview,
            })}
          >
            <Outlet />
          </main>
        </div>
      ) : (
        /* Desktop Layout */
        <div className="w-full hidden md:flex h-full min-h-0 overflow-hidden">
          <main className="flex h-full min-h-0 w-full flex-col overflow-hidden">
            <div className="mb-3 flex-shrink-0">
              <Suspense fallback={ProjectsCardSkeleton()}>
                <CardComponent className="lg:py-0 pt-2">
                  <div className="flex items-center">
                    <div className="flex-1 flex items-center px-2 overflow-hidden min-w-0">
                      <div className="w-full">
                        {loading ? (
                          <ProjectTitleSkeleton />
                        ) : error ? (
                          <div className="text-red-500 text-sm">Failed to load project</div>
                        ) : (
                          <div
                            className={clsx('lg:flex items-center gap-2 justify-between', {
                              'pointer-events-none cursor-not-allowed':
                                projectDetail?.status === 'closed' && projectDetail?.currentUserRoleName !== 'owner',
                            })}
                          >
                            <div className="text-base sm:text-lg lg:text-xl font-medium truncate mb-2 lg:mb-0 py-5">
                              {projectDetail?.title || 'Untitled Project'}
                            </div>

                            {userType !== 'client' && (
                              <div className="flex items-center gap-2 flex-wrap">
                                {userType !== 'client' && (
                                  <CustomStatusDropdown
                                    projectId={projectId!}
                                    currentStatus={projectDetail?.status || 'active'}
                                    disabled={projectDetail?.currentUserRoleName?.toLowerCase().trim() === 'client'}
                                  />
                                )}

                                {userType === 'tenant' ? (
                                  <>
                                    {!projectDetail?.company && !projectDetail?.companyId ? (
                                      <button
                                        type="button"
                                        onClick={handleAcceptProject}
                                        disabled={assigningProject}
                                        className="flex items-center truncate gap-x-1.5 rounded-md transition px-2.5 py-1.5 sm:px-3 sm:py-2 text-sm bg-btn text-btn-text hover:bg-btn-hover hover:text-btn-hover-text disabled:opacity-50 whitespace-nowrap"
                                      >
                                        {assigningProject ? 'Accepting...' : 'Accept Project'}
                                      </button>
                                    ) : (
                                      <>
                                        {projectDetail?.createdBy?.userType === 'client' && canUpdateProject && (
                                          <button
                                            type="button"
                                            onClick={handleDeclineProject}
                                            disabled={unassigningProject}
                                            className="flex items-center truncate gap-x-1.5 rounded-md transition px-2.5 py-1.5 sm:px-3 sm:py-2 text-sm bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 whitespace-nowrap"
                                          >
                                            {unassigningProject ? 'Declining...' : 'Decline Project'}
                                          </button>
                                        )}
                                      </>
                                    )}
                                    {(projectDetail?.company || projectDetail?.companyId) && canUpdateProject && (
                                      <button
                                        type="button"
                                        onClick={() => setModalOpen(true)}
                                        className="flex items-center truncate gap-x-1.5 rounded-md transition px-2.5 py-1.5 sm:px-3 sm:py-2 text-sm bg-btn text-btn-text hover:bg-btn-hover hover:text-btn-hover-text whitespace-nowrap"
                                      >
                                        <Edit className="flex-shrink-0" size={16} />
                                        <span className="hidden sm:inline">Edit Project</span>
                                      </button>
                                    )}
                                  </>
                                ) : userType === 'client' ? null : (
                                  //   <>
                                  //     {projectDetail?.company && projectDetail?.companyId && canUpdateProject && (
                                  //       <button
                                  //         type="button"
                                  //         onClick={handleDeclineProject}
                                  //         disabled={unassigningProject}
                                  //         className="flex items-center truncate gap-x-1.5 rounded-md transition px-2.5 py-1.5 sm:px-3 sm:py-2 text-sm bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 whitespace-nowrap"
                                  //       >
                                  //         {unassigningProject ? 'Declining...' : 'Decline Project'}
                                  //       </button>
                                  //     )}
                                  //     {canUpdateProject && (
                                  //       <button
                                  //         type="button"
                                  //         onClick={() => setModalOpen(true)}
                                  //         className="flex items-center truncate gap-x-1.5 rounded-md transition px-2.5 py-1.5 sm:px-3 sm:py-2 text-sm bg-btn text-btn-text hover:bg-btn-hover hover:text-btn-hover-text whitespace-nowrap"
                                  //       >
                                  //         <Edit className="flex-shrink-0" size={16} />
                                  //         <span className="hidden sm:inline">Edit Project</span>
                                  //       </button>
                                  //     )}
                                  //   </>
                                  // ) : (
                                  canUpdateProject && (
                                    <button
                                      type="button"
                                      onClick={() => setModalOpen(true)}
                                      className="flex items-center truncate gap-x-1.5 rounded-md transition px-2.5 py-1.5 sm:px-3 sm:py-2 text-sm bg-btn text-btn-text hover:bg-btn-hover hover:text-btn-hover-text whitespace-nowrap"
                                    >
                                      <Edit className="flex-shrink-0" size={16} />
                                      <span className="hidden sm:inline">Edit Project</span>
                                    </button>
                                  )
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardComponent>
              </Suspense>

              {/* Scrollable Tabs */}
              {isSwitchingProject || isPermissionResolving ? (
                <ProjectMenuItemsSkeleton />
              ) : (
                <div className="overflow-x-auto scrollbar-hide mt-2">
                  <div className="flex items-center border-b-[1px] px-2 sm:px-4 min-w-max gap-2 sm:gap-4">
                    {menuItems.map((item) => {
                      const shouldClearPreChatState =
                        item.text !== 'Chat' && location.state?.clientId && location.state?.companyId;
                      const linkState = shouldClearPreChatState ? undefined : location.state;

                      return (
                        <Link
                          key={item.id}
                          to={item.path}
                          state={linkState}
                          className={`flex items-center px-3 py-2 whitespace-nowrap text-sm sm:text-base flex-shrink-0 transition-colors ${
                            activeTab === item.path.split('/').pop()
                              ? 'text-tabClr-active border-b-2 border-tabClr-active'
                              : 'text-tabClr-inactive hover:text-tabClr-active border-b-2 border-transparent hover:border-tabClr-active'
                          }`}
                        >
                          <item.icon className="mr-2" />
                          {item.text}
                          {item.text === 'Suggested Tanent' && hasUnreadSuggestedTenant && (
                            <span className="text-red-500 ml-1 text-xl">*</span>
                          )}
                          {item.text === 'Chat' && hasUnreadProjectChat && (
                            <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-2 py-0.5 min-w-[20px] h-5 flex items-center justify-center">
                              {projectChatUnseenCount > 10 ? '10+' : projectChatUnseenCount}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                    {/* {projectDetail?.currentUserRoleName?.toLowerCase().trim() !== 'client' && (
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(window.location.href);
                            toast.success('Link copied to clipboard!');
                          }}
                          className="flex items-center px-3 py-2 whitespace-nowrap text-sm sm:text-base text-tabClr-inactive hover:text-tabClr-active border-b-2 border-transparent hover:border-tabClr-active flex-shrink-0 transition-colors"
                        >
                          <FaUsers className="mr-2" />
                          Share Project
                        </button>
                      )} */}
                  </div>
                </div>
              )}
            </div>

            <div
              className={clsx('w-full flex flex-col flex-1 min-h-0 min-w-0', {
                'pointer-events-none cursor-not-allowed':
                  projectDetail?.status === 'closed' && projectDetail?.currentUserRoleName !== 'owner',
                '[&_input]:pointer-events-none [&_textarea]:pointer-events-none [&_.ant-select]:pointer-events-none [&_.ant-picker]:pointer-events-none [&_.ant-upload]:pointer-events-none [&_input]:opacity-70 [&_textarea]:opacity-70 [&_.ant-select]:opacity-70 [&_.ant-picker]:opacity-70 [&_.ant-upload]:opacity-70':
                  isClientPreview,
              })}
            >
              <Outlet />
            </div>
          </main>
        </div>
      )}

      <EditProjectModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={projectDetail?.title || ''}
        description={projectDetail?.description || ''}
        requestedCalls={projectDetail?.requestedCalls || 0}
        insightsDeskTeam={projectDetail?.insightsDeskTeam || ''}
        insightsDeskPocNames={projectDetail?.insightsDeskPocNames || []}
        insightsDeskPocDisplayNames={projectDetail?.insightsDeskPocDisplayNames || []}
        screeningQuestions={projectDetail?.screeningQuestions || []}
        onSave={handleSave}
      />
    </>
  );
});

ProjectDetailLayout.displayName = 'ProjectDetailLayout';
